import { useSocketList } from 'src/utils/socket';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import MUIDataTable, {
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  MUIDataTableState,
  MUISortOptions,
} from 'mui-datatables';
import { useHistory } from 'react-router';
import SentinelLogo from '../../assets/images/SentinelLogo.png';
import { getFirmwareVersion, getOrgName } from '../../utils/helpers/referenceHelpers';
import { SocketContextPortal } from '../../utils/socket/context/portalSocketContext';
import { SocketContextSentinel } from '../../utils/socket/context/sentinelSocketContext';
import { DqLoading } from '../DqLoading';
import { useIntl, FormattedMessage } from 'react-intl';
import { ControllerMessages } from '../../intl/ControllerMessages';
import { GenericMessages } from '../../intl/GenericMessages';
import { useHasPermission } from 'src/utils/PermissionGuard';
import { useBle } from 'src/ble/useBle';
import { selectAuthUserInfo } from 'src/redux/auth/selectors';
import { Roles } from '@dataquiver/stoneage-roles';
import { ListOrganizationsRequest } from '../../models/organization';

interface ControllerGridItem {
  _id: string;
  description: string;
  physicalSerial: string;
  org: string;
  fw: string;
}

const ControllersGrid: React.FC = () => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const userData = useSelector(selectAuthUserInfo);
  const { connectedDevice } = useBle();
  const [columns, setColumns] = useState<MUIDataTableColumnDef[]>([]);
  const [sortOrder, setSortOrder] = useState<MUISortOptions>({
    name: 'createdAt',
    direction: 'desc',
  });

  const [fwListOptions] = useState({ query: { isHidden: 'false' } });
  const { items: firmwares } = useSocketList(SocketContextSentinel, 'firmware', fwListOptions);

  const [orgRequest] = useState<ListOrganizationsRequest>({
    statuses: ['active'],
    pageNumber: 0,
    pageSize: 1000,
    sort: { order: 'asc', property: 'orgName' },
  });
  const { items: organizations } = useSocketList(SocketContextPortal, 'organizations', orgRequest, 'listV2');

  const ctrlQuery = React.useMemo(() => {
    return Roles.isStoneAge(userData?.role) ? {} : { org: userData?.org };
  }, [userData?.org, userData?.role]);

  const { items: controllers, fetched } = useSocketList(SocketContextSentinel, 'controllers', ctrlQuery);

  // Users should only see the controller they are currently connected to unless they have permission
  const canViewNonConnectedControllers = useHasPermission('ViewNonConnectedControllers');

  const gridItems = React.useMemo(() => {
    return controllers
      .filter(c => canViewNonConnectedControllers || connectedDevice?.serialNumber === c.physicalSerial)
      .map(controller => {
        const result: ControllerGridItem = {
          _id: controller._id,
          description: controller.description,
          physicalSerial: controller.physicalSerial,
          org: getOrgName(controller.org, organizations),
          fw: getFirmwareVersion(controller.fw, firmwares),
        };
        return result;
      });
  }, [canViewNonConnectedControllers, connectedDevice?.serialNumber, controllers, firmwares, organizations]);

  const handleView = (data: any) => {
    // data[4] holds the software serial number used to lookup the ctrl via API
    history.push(`/controllers/${data[4]}`);
  };

  useEffect(() => {
    setColumns([
      {
        label: formatMessage(ControllerMessages.controller),
        name: 'physicalSerial',
        options: {
          filter: false,
        },
      },
      {
        label: formatMessage(GenericMessages.descriptionLabel),
        name: 'description',
        options: {
          filter: false,
        },
      },
      {
        label: formatMessage(ControllerMessages.controllerGridFirmwareVersionColumn),
        name: 'fw',
      },
      {
        label: formatMessage(GenericMessages.orgLabel),
        name: 'org',
      },
      {
        label: formatMessage(ControllerMessages.controllerGridSoftwareSerialColumn),
        name: '_id', // must be column 0! That way it is data[0] on rowClick. Thanks.
        options: {
          filter: false,
          display: false,
        },
      },
    ]);
  }, [formatMessage]);

  const handleTableChange = (eventType: string, tableState: MUIDataTableState) => {
    switch (eventType) {
      case 'viewColumnsChange':
        // It will only accept data like this, but it sends it back flattened
        const tempColumns = tableState.columns.map(column => ({ ...column, options: { ...column } }));
        setColumns(tempColumns);
        break;
      case 'sort':
        setSortOrder(tableState.sortOrder);
        break;
    }
  };

  const options = {
    selectableRows: 'none',
    responsive: 'standard',
    onRowClick: handleView,
    onTableChange: (eventType: string, tableState: MUIDataTableState) => handleTableChange(eventType, tableState),
    rowsPerPageOptions: [10, 25, 50],
    print: false,
    download: false,
    sortOrder: sortOrder,
    textLabels: {
      body: {
        noMatch: !fetched ? (
          <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
            <DqLoading width="100px" img={SentinelLogo} />
          </div>
        ) : (
          <FormattedMessage {...ControllerMessages.noControllersMatchSearch} />
        ),
        toolTip: formatMessage(GenericMessages.dataTablesSort),
      },
      pagination: {
        next: formatMessage(GenericMessages.dataTablesNextPage),
        previous: formatMessage(GenericMessages.dataTablesPreviousPage),
        rowsPerPage: formatMessage(GenericMessages.dataTablesRowsPerPage),
        displayRows: formatMessage(GenericMessages.of),
      },
      toolbar: {
        search: formatMessage(GenericMessages.searchPlaceholder),
        viewColumns: formatMessage(GenericMessages.dataTablesViewColumns),
        filterTable: formatMessage(GenericMessages.dataTablesFilterTable),
      },
      filter: {
        all: formatMessage(GenericMessages.dataTablesAll),
        title: formatMessage(GenericMessages.dataTablesFilters),
        reset: formatMessage(GenericMessages.dataTablesReset),
      },
      viewColumns: {
        title: formatMessage(GenericMessages.dataTablesShowColumns),
      },
    },
  } as MUIDataTableOptions;

  return <MUIDataTable columns={columns} data={gridItems} options={options} title={undefined} />;
};

export default ControllersGrid;
